import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import { useDropzone } from "react-dropzone";
import { useState, useEffect } from "react";
import resizeAndConvertToJPG from "util/resize_image";
import { TUploadImageType, WheelBrandListItem } from "../types/wheel-brand";

interface CategoryDetailModalProps {
  showWheelImgModal: boolean;
  wheelItemPriceSelected: WheelBrandListItem | null;
  handleClose: () => void;
  onDeleteImage: (type: TUploadImageType) => Promise<void>;
  onUploadImage: (type: TUploadImageType, file: File) => Promise<void>;
}

/**
 * WheelBrandDetailModal Component
 *
 * Modal dialog for uploading, previewing, and deleting the main image of a wheel brand.
 * Supports drag-and-drop image upload using react-dropzone and displays the current image if available.
 * @param {object} props - Component props.
 * @param {boolean} props.showWheelImgModal - Whether the modal is open.
 * @param {WheelBrandListItem|null} props.wheelItemPriceSelected - The selected wheel brand item, or null.
 * @param {() => void} props.handleClose - Callback to close the modal.
 * @param {(type: TUploadImageType) => void} props.onDeleteImage - Callback to delete the image.
 * @param {(type: TUploadImageType, file: File) => void} props.onUploadImage - Callback to upload a new image.
 * @returns The rendered modal for wheel brand image management.
 */
function WheelBrandDetailModal({
  showWheelImgModal,
  wheelItemPriceSelected,
  handleClose,
  onDeleteImage,
  onUploadImage,
}: CategoryDetailModalProps) {
  const [imageCacheBuster, setImageCacheBuster] = useState<number>(Date.now());

  const onDrop = async (acceptedFiles: File[]) => {
    // Handle the dropped files
    if (acceptedFiles.length > 0) {
      const resizedImage = await resizeAndConvertToJPG(acceptedFiles[0]);
      await onUploadImage("main", resizedImage);
      // Update cache buster after successful upload to force image reload
      setImageCacheBuster(Date.now());
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: {
      "image/*": [],
    },
  });

  // Helper function to add cache buster to image URL
  const getImageUrlWithCacheBuster = (url: string | null) => {
    if (!url) return null;
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}t=${imageCacheBuster}`;
  };

  // Reset cache buster when modal opens or wheelItemPriceSelected changes
  useEffect(() => {
    if (showWheelImgModal && wheelItemPriceSelected) {
      setImageCacheBuster(Date.now());
    }
  }, [showWheelImgModal, wheelItemPriceSelected?.wheel_image_url_main]);

  return (
    <Modal open={showWheelImgModal} onClose={handleClose}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          maxHeight: "90vh",
          overflow: "auto",
          width: "80%",
          maxWidth: "818px",
          boxSizing: "border-box",
          backgroundColor: "var(--white)",
          border: "3px solid var(--gray-250)",
          padding: 3,
          borderRadius: "21px",
        }}
      >
        <Box display="flex" justifyContent="space-between" alignItems="center" marginBottom="30px">
          <Box display="flex">
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: "fontWeightMedium",
                color: "var(--gray-600)",
                marginRight: "27px",
                fontSize: "33px",
              }}
            >
              代表画像アップ
            </Typography>
          </Box>
          <Button
            variant="outlined"
            onClick={handleClose}
            sx={{
              backgroundColor: "var(--white)",
              fontSize: "25px",
              height: "62px",
              borderRadius: "10px",
              fontWeight: "fontWeightMedium",
              border: "3px solid var(--gray-250)",
              color: "var(--gray-600)",
              ":hover": {
                border: "3px solid var(--info)",
                color: "var(--info)",
              },
            }}
          >
            閉じる
          </Button>
        </Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" columnGap="40px">
          <Box
            display="flex"
            flexDirection="column"
            padding="92px 25px 20px"
            border="2px solid var(--gray-250)"
            height="355px"
            width="351px"
            alignItems="center"
            justifyContent="space-between"
            boxSizing="border-box"
            marginLeft="12px"
            {...getRootProps()}
          >
            <input hidden type="file" {...getInputProps()} />
            <Typography
              variant="h4"
              component="h2"
              sx={{
                fontWeight: "fontWeightMedium",
                color: "var(--gray-600)",
                fontSize: "26px",
              }}
            >
              ドラッグ＆ドロップで <br />
              画像を登録/更新できます
            </Typography>
            <Box
              component="label"
              sx={{
                border: "1px solid var(--gray-250)",
                backgroundColor: "var(--white)",
                color: "var(--black)",
                fontSize: { xs: 16, md: 18, lg: 20 },
                fontWeight: "bold",
                borderRadius: "4px",
                width: "172px",
                height: "40px",
                whiteSpace: "pre",
                boxShadow: "var(--shadow)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
              }}
            >
              ファイルを選択
            </Box>
          </Box>
          {wheelItemPriceSelected?.wheel_image_url_main && (
            <Box
              sx={{
                flex: "1",
                display: "flex",
                justifyContent: "center",
                flexDirection: "column",
                alignItems: "center",
                rowGap: "30px",
              }}
            >
              <img
                src={
                  getImageUrlWithCacheBuster(wheelItemPriceSelected?.wheel_image_url_main) ||
                  undefined
                }
                alt="wheel brand"
                width="289px"
                style={{ height: "275px" }}
                key={imageCacheBuster} // Force re-render when cache buster changes
              />
              <Button
                variant="outlined"
                sx={{
                  border: "1px solid var(--gray-250)",
                  backgroundColor: "var(--white)",
                  color: "var(--black)",
                  fontSize: { xs: 16, md: 18, lg: 20 },
                  fontWeight: "bold",
                  borderRadius: "4px",
                  width: "172px",
                  height: "40px",
                  whiteSpace: "pre",
                  boxShadow: "var(--shadow)",
                }}
                disabled={!wheelItemPriceSelected?.wheel_image_url_main}
                onClick={() => onDeleteImage("main")}
              >
                代表画像削除
              </Button>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
}

export default WheelBrandDetailModal;
